import React, { useState, useEffect } from 'react';
import {
    Box,
    TextField,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    Button,
    Alert,
    Checkbox,
    FormGroup,
} from '@mui/material';
import { User } from '../types/shared';
import { useTranslation } from 'react-i18next';

export interface UserProfileData {
    location: string;
    experienceLevel: 'Beginner' | 'Intermediate' | 'Expert';
    growingInterests: string[];
    gardenGoals: string[];
    foodPreferences: string;
    availability: string;
    language: string;
}

interface UserProfileFormProps {
    initialValues: Partial<User> | null;
    onSave: (profile: UserProfileData) => void;
    saveLabel?: string;
    loading?: boolean;
}

const EXPERIENCE_LEVELS = ['Beginner', 'Intermediate', 'Expert'];
const GROWING_INTERESTS = ['Vegetables', 'Flowers', 'Herbs', 'Fruit'];
const GARDEN_GOALS = ['Self-Sufficiency', 'Optimize Yield', 'Aesthetics', 'Pollinator Friendly', 'Education', 'Low Maintenance', 'Grow from Seed', 'Harvest Own Seeds', 'Organic Practices', 'Rare Varieties'];

const UserProfileForm: React.FC<UserProfileFormProps> = ({ initialValues, onSave, saveLabel, loading = false }) => {
    const { t } = useTranslation();
    const [profile, setProfile] = useState<UserProfileData>({
        location: '',
        experienceLevel: 'Beginner',
        growingInterests: [],
        gardenGoals: [],
        foodPreferences: '',
        availability: '',
        language: 'en',
    });
    const [error, setError] = useState<string>('');

    useEffect(() => {
        if (initialValues) {
            setProfile(prev => ({
                ...prev,
                location: initialValues.location || '',
                experienceLevel: initialValues.experienceLevel || 'Beginner',
                growingInterests: initialValues.growingInterests || (initialValues as any).gardenTypes || [],
                gardenGoals: initialValues.gardenGoals || [],
                foodPreferences: initialValues.foodPreferences || '',
                availability: initialValues.availability || '',
                language: initialValues.language || 'en',
            }));
        }
    }, [initialValues]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setProfile({ ...profile, [name]: value });
        if (name === 'location' && value.trim() !== '') {
            setError('');
        }
    };

    const handleExperienceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setProfile({ ...profile, experienceLevel: e.target.value as any });
    };

    const handleGrowingInterestChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const newInterests = e.target.checked
            ? [...profile.growingInterests, value]
            : profile.growingInterests.filter(t => t !== value);
        setProfile({ ...profile, growingInterests: newInterests });
    };

    const handleGardenGoalChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        const newGoals = e.target.checked
            ? [...profile.gardenGoals, value]
            : profile.gardenGoals.filter(g => g !== value);
        setProfile({ ...profile, gardenGoals: newGoals });
    };

    const handleSubmit = () => {
        if (!profile.location || profile.location.trim() === '') {
            setError(t('settings.location_required'));
            return;
        }
        onSave(profile);
    };

    return (
        <Box sx={{ mt: 2 }}>
            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            <TextField
                label={t('settings.location_label')}
                name="location"
                value={profile.location || ''}
                onChange={handleChange}
                fullWidth
                margin="normal"
                required
                error={!!error && (!profile.location || profile.location.trim() === '')}
                helperText={t('settings.location_helper')}
            />

            {/* Experience Level */}
            <Box sx={{ mt: 3, mb: 2 }}>
                <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend" sx={{ textAlign: 'left' }}>{t('settings.experience_level')}</FormLabel>
                    <RadioGroup
                        aria-label="experience-level"
                        name="experienceLevel"
                        value={profile.experienceLevel}
                        onChange={handleExperienceChange}
                        row
                        sx={{ mt: 1 }}
                    >
                        {EXPERIENCE_LEVELS.map(level => (
                            <FormControlLabel
                                key={level}
                                value={level}
                                control={<Radio />}
                                label={level}
                            />
                        ))}
                    </RadioGroup>
                </FormControl>
            </Box>

            {/* Language Selection */}
            <Box sx={{ mt: 3, mb: 2 }}>
                <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend" sx={{ textAlign: 'left' }}>{t('settings.preferred_language')}</FormLabel>
                    <RadioGroup
                        aria-label="language"
                        name="language"
                        value={profile.language}
                        onChange={handleChange}
                        row
                        sx={{ mt: 1 }}
                    >
                        <FormControlLabel value="en" control={<Radio />} label="English" />
                        <FormControlLabel value="de" control={<Radio />} label="Deutsch" />
                        <FormControlLabel value="fr" control={<Radio />} label="Français" />
                    </RadioGroup>
                </FormControl>
            </Box>

            {/* Growing Interests */}
            <Box sx={{ mt: 3, mb: 2 }}>
                <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend" sx={{ textAlign: 'left' }}>{t('settings.what_to_grow')}</FormLabel>
                    <FormGroup sx={{ flexDirection: 'row', mt: 1 }}>
                        {GROWING_INTERESTS.map(type => (
                            <FormControlLabel
                                key={type}
                                control={
                                    <Checkbox
                                        checked={profile.growingInterests.includes(type)}
                                        onChange={handleGrowingInterestChange}
                                        value={type}
                                    />
                                }
                                label={type}
                                sx={{ width: '50%' }}
                            />
                        ))}
                    </FormGroup>
                </FormControl>
            </Box>

            {/* Garden Goals */}
            <Box sx={{ mt: 3, mb: 2 }}>
                <FormControl component="fieldset" fullWidth>
                    <FormLabel component="legend" sx={{ textAlign: 'left' }}>{t('settings.main_goals')}</FormLabel>
                    <FormGroup sx={{ flexDirection: 'row', mt: 1 }}>
                        {GARDEN_GOALS.map(goal => (
                            <FormControlLabel
                                key={goal}
                                control={
                                    <Checkbox
                                        checked={profile.gardenGoals.includes(goal)}
                                        onChange={handleGardenGoalChange}
                                        value={goal}
                                    />
                                }
                                label={goal}
                                sx={{ width: '50%' }}
                            />
                        ))}
                    </FormGroup>
                </FormControl>
            </Box>

            <TextField
                label={t('settings.food_preferences')}
                name="foodPreferences"
                value={profile.foodPreferences || ''}
                onChange={handleChange}
                fullWidth
                multiline
                rows={3}
                margin="normal"
                helperText={t('settings.food_helper')}
            />

            <TextField
                label={t('settings.weekly_availability')}
                name="availability"
                value={profile.availability || ''}
                onChange={handleChange}
                fullWidth
                margin="normal"
                helperText={t('settings.availability_helper')}
            />

            <Button
                variant="contained"
                onClick={handleSubmit}
                fullWidth={false}
                sx={{ mt: 3 }}
                disabled={loading}
            >
                {loading ? t('settings.saving') : (saveLabel || "Save")}
            </Button>
        </Box>
    );
};

export default UserProfileForm;
